// src/support/tileRuntimeContext.ts
import { Query } from '../types/tileBuilderTypes';

/**
 * Runtime context passed to tiles at render time.
 *
 * Available variables for substitution:
 * - {email} - Current user/manager email
 * - {managerEmail} - Explicit manager email
 * - {userEmail} - Individual user email
 * - {domain} - 'salesforce' | 'workshops' | 'campaigns' | 'enablement' | 'pov'
 * - {teamEmailsList} - Comma-separated list of team emails
 * - {teamSize} - Number of team members
 * - {viewMode} - 'team' or 'individual'
 */
export interface TileRuntimeContext {
  email?: string;
  managerEmail?: string;
  userEmail?: string;
  domain?: 'salesforce' | 'workshops' | 'campaigns' | 'enablement' | 'pov';
  teamScope?: string[];
  teamEmailsList?: string;
  teamSize?: number;
  viewMode?: 'team' | 'individual';
  isTeamView?: boolean;
  isIndividualView?: boolean;
  dateRange?: { start: string; end: string };
  current_time_filter?: string;
  [key: string]: any;
}


/**
 * Substitute runtime variables in a string template.
 * Handles both simple string values and complex types (arrays, objects) in context.
 */
export function substituteRuntimeVariables(
  template: string,
  userVars: Record<string, string>,
  runtimeContext: TileRuntimeContext
): string {
  const allVars: Record<string, string> = { ...userVars };

  for (const [key, value] of Object.entries(runtimeContext)) {
    if (value == null) {
      allVars[key] = '';
    } else if (typeof value === 'string') {
      allVars[key] = value;
    } else if (Array.isArray(value)) {
      allVars[key] = value.join(',');
    } else if (typeof value === 'object') {
      allVars[key] = JSON.stringify(value);
    } else {
      allVars[key] = String(value);
    }
  }

  return template.replace(/\{(\w+)\}/g, (_, key) => {
    if (allVars[key] != null) return allVars[key];
    return `{${key}}`;
  });
}

/**
 * Check if a query should execute based on conditional execution rules
 */
export function shouldExecuteQuery(
  query: Query,
  context: TileRuntimeContext
): boolean {
  if (!query.conditionalExecution) return true;

  const { whenField, equals } = query.conditionalExecution;
  return context[whenField] === equals;
}